import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Droplets, Flame, Package, Play, ShieldCheck, Train, Zap } from "lucide-react";
import { useT } from "@/i18n/LocaleContext";

const HERO_IMG = "/assets/hero-laser.jpg";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] },
});

const primeVideo = (v) => {
  if (!v) return;
  v.muted = true;
  v.setAttribute("muted", "");
  v.setAttribute("webkit-playsinline", "");
};
const safePlay = (v) => { if (!v) return; const p = v.play(); if (p && typeof p.catch === "function") p.catch(() => {}); };
const safePause = (v) => { if (!v) return; try { v.pause(); } catch { /* noop */ } };

export default function Hero() {
  const { t, dict, locale } = useT();
  const inquiryPath = locale === "en" ? "/en/inquiry-portal" : "/anfrage-portal";
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  const [showPlayHint, setShowPlayHint] = useState(false);

  const playDefault = () => { safePause(rightVideoRef.current); safePlay(leftVideoRef.current); };
  const onEnterLeft = () => { safePause(rightVideoRef.current); safePlay(leftVideoRef.current); };
  const onEnterRight = () => { safePause(leftVideoRef.current); safePlay(rightVideoRef.current); setShowPlayHint(false); };
  const onLeaveHero = () => playDefault();

  useEffect(() => {
    primeVideo(leftVideoRef.current);
    primeVideo(rightVideoRef.current);
    safePlay(leftVideoRef.current);
    safePause(rightVideoRef.current);
    const tryDefault = () => playDefault();
    const events = ["touchstart", "pointerdown", "scroll"];
    events.forEach((e) => document.addEventListener(e, tryDefault, { once: true, passive: true }));
    const timer = setTimeout(() => {
      const l = leftVideoRef.current;
      if (l && (l.paused || l.readyState < 2)) setShowPlayHint(true);
    }, 2500);
    const l = leftVideoRef.current;
    const onPlaying = () => setShowPlayHint(false);
    l && l.addEventListener("playing", onPlaying);
    return () => {
      events.forEach((e) => document.removeEventListener(e, tryDefault));
      clearTimeout(timer);
      l && l.removeEventListener("playing", onPlaying);
    };
  }, []);

  const hygieneList = dict.hero.hygieneList;
  const contractList = dict.hero.contractList;
  const ICONS = [Droplets, Train, ShieldCheck];
  const CONTRACT_ICONS = [Zap, Flame, Package];
  const LINES = [t("hero.contractTitle1"), t("hero.contractTitle2")];

  return (
    <section className="grid lg:flex lg:min-h-screen" style={{ paddingTop: "var(--header-h, 112px)" }} data-testid="hero-section" onMouseLeave={onLeaveHero}>
      {/* Weg A — Hygiene & Mobilität */}
      <div
        className="hero-panel hero-panel-left relative overflow-hidden bg-white flex flex-col px-6 sm:px-12 lg:px-16 xl:px-24 py-24 lg:py-32 border-b lg:border-b-0 lg:border-r border-[#E3E8EC]"
        onMouseEnter={onEnterLeft}
      >
        <video
          ref={leftVideoRef}
          className="hero-media absolute inset-0 w-full h-full object-cover"
          muted loop playsInline disablePictureInPicture preload="auto" aria-hidden="true"
          data-testid="hero-video-left"
        >
          <source src="/assets/hero-hygiene.mp4" type="video/mp4" />
          <source src="/assets/hero-hygiene.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-white/85 backdrop-blur-sm z-[1]" aria-hidden="true" />
        <div className="hero-content relative z-[2] flex flex-col flex-1 w-full max-w-[520px]">
          <motion.h2 {...fadeUp(0.2)} className="text-4xl sm:text-5xl xl:text-6xl font-black uppercase tracking-tighter leading-[1.02] text-[var(--ci-grey)]" data-testid="hero-hygiene-headline">
            {t("hero.hygieneTitle1")}<br /><span className="text-[var(--ci-blue)]">{t("hero.hygieneTitle2")}</span>
          </motion.h2>
          <motion.p {...fadeUp(0.32)} className="mt-8 text-base sm:text-lg text-[var(--ci-grey)] leading-relaxed">
            {t("hero.hygieneCopy")}
          </motion.p>
          <motion.ul {...fadeUp(0.44)} className="mt-10 space-y-5">
            {hygieneList.map((label, i) => {
              const Icon = ICONS[i] || ShieldCheck;
              return (
                <li key={label} className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-[var(--ci-blue-light)] text-[var(--ci-blue)] flex items-center justify-center shrink-0"><Icon size={17} /></span>
                  <span className="text-sm font-medium text-[var(--ci-grey)]">{label}</span>
                </li>
              );
            })}
          </motion.ul>
          <motion.div {...fadeUp(0.56)} className="mt-auto pt-12">
            <button onClick={() => scrollTo("hygiene")} data-testid="hero-hygiene-btn"
              className="flex items-center gap-3 px-8 h-14 rounded-full border-2 border-[var(--ci-grey)] text-[var(--ci-grey)] hover:bg-[var(--ci-grey)] hover:text-white font-tech text-xs tracking-[0.2em] uppercase transition-colors duration-200 active:scale-[0.98]">
              {t("hero.hygieneCta")} <ArrowUpRight size={16} />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Weg B — Lohnfertigung */}
      <div
        className="hero-panel relative overflow-hidden flex flex-col px-6 sm:px-12 lg:px-16 xl:px-24 py-24 lg:py-32 min-h-[75vh]"
        onMouseEnter={onEnterRight}
      >
        <video ref={rightVideoRef} className="hero-media absolute inset-0 w-full h-full object-cover" muted loop playsInline disablePictureInPicture preload="auto" poster={HERO_IMG} data-testid="hero-video" aria-label={locale === "en" ? "Fiber laser cutting head during sheet metal machining" : "Faserlaser-Schneidanlage bei der Bearbeitung von Blech"}>
          <source src="/assets/hero-flycut.mp4" type="video/mp4" />
          <source src="/assets/hero-flycut.webm" type="video/webm" />
        </video>
        <div className="hero-overlay absolute inset-0 bg-gradient-to-r from-[#111315]/85 via-[#111315]/60 to-[#111315]/40" />
        {showPlayHint && (
          <button
            onClick={() => { const v = rightVideoRef.current; if (v) { v.muted = true; safePlay(v); setShowPlayHint(false); } }}
            data-testid="hero-video-play"
            aria-label={locale === "en" ? "Play video" : "Video abspielen"}
            className="absolute bottom-8 right-8 z-10 w-14 h-14 rounded-full bg-[var(--ci-blue)] hover:bg-[var(--ci-turquoise)] text-white flex items-center justify-center shadow-lg transition-colors duration-200 active:scale-95"
          >
            <Play size={22} fill="currentColor" className="ml-0.5" />
          </button>
        )}
        <div className="hero-content relative flex flex-col flex-1 w-full max-w-[520px]">
          <h2 className="font-black uppercase tracking-tighter leading-[1.02] text-4xl sm:text-5xl xl:text-6xl text-white" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line + i} className="block overflow-hidden pb-2">
                <motion.span className={`block ${i === LINES.length - 1 ? "text-[var(--ci-turquoise)]" : ""}`} initial={{ y: "115%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.4 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h2>
          <motion.p {...fadeUp(0.75)} className="mt-8 text-base sm:text-lg text-white/80 leading-relaxed" data-testid="hero-subline">
            {t("hero.contractCopy")}
          </motion.p>
          <motion.ul {...fadeUp(0.85)} className="mt-10 space-y-5" data-testid="hero-contract-list">
            {contractList.map((label, i) => {
              const Icon = CONTRACT_ICONS[i] || Package;
              return (
                <li key={label} className="flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm text-[var(--ci-turquoise)] flex items-center justify-center shrink-0"><Icon size={17} /></span>
                  <span className="text-sm font-medium text-white/90">{label}</span>
                </li>
              );
            })}
          </motion.ul>
          <motion.div {...fadeUp(0.95)} className="mt-auto pt-11 flex flex-wrap items-center gap-7">
            <Link to={inquiryPath} data-testid="hero-cta-btn"
              className="flex items-center gap-3 px-8 h-14 rounded-full bg-[var(--ci-blue)] hover:bg-[var(--ci-turquoise)] text-white font-tech text-xs tracking-[0.2em] uppercase transition-all duration-200 active:scale-[0.98] shadow-[0_10px_36px_rgba(44,87,210,0.45)]">
              {t("hero.contractCta")} <ArrowRight size={16} />
            </Link>
            <span className="font-tech text-[10px] tracking-[0.18em] uppercase text-white/60">{t("hero.contractBadge")}</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
